import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Loader from 'components/D3Loader'
import ScheduleStepPage from '../components/ScheduleStepPage'
import { ScheduleDraftProvider } from '../context/ScheduleDraftProvider'
import PersistSchedulingEngineDraft from '../components/PersistSchedulingEngineDraft'
import { useEntityOptions } from '../hooks/useEntityOptions'
import { readEngineSchedules } from '../api/engineSchedulesApi'
import { getEngineScheduleRevision } from '../models/normalizeDraft'
import { resolveScheduleDraftBootstrap } from '../utils/resolveScheduleDraftBootstrap'
import { selectLeafSitesForAudience } from '../hooks/useEntityOptions'
import {
  getStudyVersionID,
  isVersionUatOrLive,
} from 'utils/studyConfigUtils'
import { schedulerActions } from 'routes/Studies/routes/Study/routes/Instruments/routes/Instrument/modules/Instrument'
import '../styles/schedulingEngine.scss'

const SchedulingEngineView = props => {
  const {
    instrument,
    loading,
    study,
    studyID,
    visitTemplates,
    studyVisitSchedule,
    instruments,
    sitesList,
    sitesMap,
    tracks,
    cohortMap,
    dispatch,
    isReadOnlyState,
    onPromptDeploy,
    location,
    schedulingEngineDraft,
    studyVersionID,
    hasStudyPromotion,
  } = props

  const entityOptions = useEntityOptions({
    visitTemplates,
    studyVisitSchedule,
    instruments,
    sites: sitesList,
    sitesMap,
    tracks,
    cohortMap,
    study,
    currentInstrumentId: instrument?.id,
  })
  const { schedules: persistedSchedules } = readEngineSchedules(instrument)
  const scheduleRevision = getEngineScheduleRevision(instrument)
  const { schedules, initialScheduleIndex } = resolveScheduleDraftBootstrap({
    instrumentId: instrument?.id,
    persistedSchedules,
    schedulingEngineDraft,
    location,
  })

  if (loading && !instrument?.id) {
    return Loader(props)
  }

  return (
    <ScheduleDraftProvider
      key={instrument?.id || 'new'}
      initialSchedules={schedules}
      initialScheduleIndex={initialScheduleIndex}
      scheduleRevision={scheduleRevision}>
      <PersistSchedulingEngineDraft instrumentId={instrument?.id} dispatch={dispatch} />
      <ScheduleStepPage
        entityOptions={entityOptions}
        study={study}
        studyID={studyID}
        instrument={instrument}
        dispatch={dispatch}
        disabled={isReadOnlyState}
        onPromptDeploy={onPromptDeploy}
        studyVersionID={studyVersionID}
        hasStudyPromotion={hasStudyPromotion}
      />
    </ScheduleDraftProvider>
  )
}

SchedulingEngineView.propTypes = {
  instrument: PropTypes.object,
  loading: PropTypes.bool,
  study: PropTypes.object,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visitTemplates: PropTypes.array,
  studyVisitSchedule: PropTypes.array,
  instruments: PropTypes.array,
  sitesList: PropTypes.array,
  tracks: PropTypes.array,
  dispatch: PropTypes.func,
  isReadOnlyState: PropTypes.bool,
  onPromptDeploy: PropTypes.func,
  location: PropTypes.object,
  schedulingEngineDraft: PropTypes.object,
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasStudyPromotion: PropTypes.bool,
}

const mapDispatch = dispatch => ({
  dispatch,
  onPromptDeploy: studyID => dispatch(schedulerActions.promptDeployInstrument(studyID)),
})

const mapState = state => {
  const {
    instrumentReducer,
    instruments,
    loading,
    participantReducer,
    sites,
    study,
    participants,
    visitSchedules,
    location,
  } = state
  const studyVersionID = getStudyVersionID(location)
  const { currentStudy, studyVersionList } = study
  const hasStudyPromotion = !!currentStudy?.config?.study_promotion
  return {
    instrument: instrumentReducer?.instrument,
    instruments: instruments?.allInstruments || instruments?.instrumentList || [],
    loading: loading?.general,
    visitTemplates: participantReducer?.visitTemplates || [],
    studyVisitSchedule: visitSchedules?.studyVisitSchedule || [],
    sitesList: selectLeafSitesForAudience(sites),
    sitesMap: sites?.sitesMap || {},
    tracks: participants?.cohortList || [],
    cohortMap: participants?.cohortMap || {},
    study: currentStudy,
    studyID: currentStudy?.id,
    isReadOnlyState: hasStudyPromotion && isVersionUatOrLive(studyVersionList, studyVersionID),
    location,
    schedulingEngineDraft: instrumentReducer?.schedulingEngineDraft,
    studyVersionID,
    hasStudyPromotion: !!hasStudyPromotion,
  }
}

export default connect(mapState, mapDispatch)(SchedulingEngineView)
